<template>
    <!--
        THE ADMIN SHELL'S LEFT COLUMN: a brand row, the grouped menu, and the signed-in user at
        the bottom — the three things a console's sidebar is for, in the order they are looked at.

        It paints `--ui-bg-chrome` — the project surface `theme/theme.css` declares and
        the house theme separates from the canvas. THE FRAME IS ONE SURFACE, and in
        this theme that surface is the PANEL colour in both modes: white in light, and in dark
        the same mix the cards take, both standing on a darker canvas. The hairline on the
        right edge is the seam between the frame and the page. A theme that says nothing leaves
        the variable at `--ui-bg` and gets the flat single-surface shell instead.

        Opaque, not a tint over the canvas: on mobile this column is the overlay.
    -->
    <nav class="flex flex-col w-64 shrink-0 border-r border-default bg-(--ui-bg-chrome)">
        <div class="h-(--ui-header-height) shrink-0 flex items-center gap-2.5 px-4 border-b border-default">
            <img src="/logo.svg" alt="" class="size-7" >
            <div class="leading-tight min-w-0 flex-1">
                <p class="text-base font-semibold text-highlighted truncate">{{ $t('brand.product_name') }}</p>
            </div>

            <!--
                THE WAY OUT, on mobile only. Open, this column IS the overlay and it covers the
                topbar's hamburger, so the affordance that opened the menu cannot also close it.
            -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-x"
                :aria-label="$t('layout.close_menu')"
                class="-mr-1 shrink-0"
                @click="closeMenu"
            />
        </div>

        <div class="flex flex-col gap-5 flex-1 overflow-y-auto px-3 py-4">
            <div v-for="(group, index) in groups" :key="`group-${index}`" class="flex flex-col gap-1">
                <p
                    v-if="group.labelKey"
                    class="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-widest text-muted"
                >{{ $t(group.labelKey) }}</p>

                <UNavigationMenu
                    orientation="vertical"
                    :items="group.items"
                    :ui="{ link: 'py-2' }"
                />
            </div>
        </div>

        <!-- Nastavitve sits apart at the bottom, above the person, as in the mockups. -->
        <div class="shrink-0 px-3 pb-2">
            <UNavigationMenu orientation="vertical" :items="settingsItems" :ui="{ link: 'py-2' }" />
        </div>

        <!--
            The signed-in person, where an admin console puts it: their name, and the tenant and
            role they work as ("Hotel Lipa · Skrbnik"). It carries the sign-out action too.
        -->
        <div class="shrink-0 border-t border-default p-3">
            <UDropdownMenu :items="userMenu" :content="{ align: 'start', side: 'top' }" class="w-full">
                <button
                    type="button"
                    class="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-elevated transition-colors"
                >
                    <span class="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                        {{ userInitials }}
                    </span>

                    <span class="min-w-0 flex-1 leading-tight">
                        <span class="block truncate text-sm font-medium text-highlighted">{{ userName }}</span>
                        <span class="block truncate text-xs text-muted" :title="userRole">{{ userRole }}</span>
                    </span>

                    <UIcon name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
                </button>
            </UDropdownMenu>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import type { NavigationMenuItem } from '@nuxt/ui'
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useRoute, useRouter } from 'vue-router'

    import { NAV_GROUPS, SETTINGS_ITEM, useLayout, type NavItem } from '@/layouts/layout'
    import { PENDING_POLL_CALM_MS, usePendingPoll } from '@/libs/pendingScreens'
    import { initials, roleLabel } from '@/libs/user'
    import { useAppStore } from '@/stores/app'
    import { usePendingStore } from '@/stores/pending'
    import { useTenantStore } from '@/stores/tenant'

    /**
     * The menu. The MODEL lives in `layouts/layout.ts` (`NAV_GROUPS`), because the topbar titles
     * the page from the same list.
     *
     * It is a `UNavigationMenu orientation="vertical"` per group, not one menu with `type:
     * 'label'` rows: a group is a separate list with its own header.
     *
     * THE ACTIVE ROW IS A SOFT PRIMARY PLATE — light blue behind blue text, as in the mockups.
     * The component's `pill` variant already colours an active row's label and icon
     * `text-primary`; one class per item turns its neutral plate into `before:bg-primary/10`.
     *
     * DIGITAL SIGNAGE: the tenant's pages link to the CURRENT tenant (`/t/:tid/…`) and are left
     * out while there is none (a provider admin without customers); the tenant's own users are
     * the entry of an admin OF THAT TENANT, the "Ponudnik" group a provider admin's.
     */
    const { t }                   = useI18n()
    const route                   = useRoute()
    const router                  = useRouter()
    const appStore                = useAppStore()
    const tenantStore             = useTenantStore()
    const pendingStore            = usePendingStore()
    const { isMobile, closeMenu } = useLayout()

    /*
     * "Zasloni" carries the number of displays waiting to be confirmed there (docs/API.md →
     * Pending screens) — asked every 30 s for an admin of the tenant; Zasloni asks every 5 s.
     */
    usePendingPoll(computed((): string => tenantStore.contextTid), PENDING_POLL_CALM_MS)

    const PENDING_BADGE_ITEM = 'screens'

    const toItem = (item: NavItem, tid: string): NavigationMenuItem => {
        const active = item.routes.includes(String(route.name ?? ''))

        const pending = item.name === PENDING_BADGE_ITEM && pendingStore.tid === tid ? pendingStore.count : 0

        return {
            label:    t(item.labelKey),
            icon:     item.icon,
            to:       tid ? { name: item.name, params: { tid } } : { name: item.name },
            badge:    pending > 0 ? { label: String(pending), color: 'primary', variant: 'solid', size: 'sm' } : undefined,
            active,
            class:    active ? 'before:bg-primary/10' : '',
            onSelect: () => { closeMenu() },
        }
    }

    const groups = computed((): { labelKey: string | null, items: NavigationMenuItem[] }[] => {
        const tid = tenantStore.contextTid

        return NAV_GROUPS
            .filter((group) => (!group.providerOnly || appStore.isProviderAdmin()) && (!group.tenant || !!tid))
            .map((group) => ({
                labelKey: group.labelKey,
                items:    group.items
                    .filter((item) => !item.tenantAdminOnly || (appStore.isAdmin(tid) && !appStore.isProviderAdmin()))
                    .map((item) => toItem(item, group.tenant ? tid : '')),
            }))
    })

    const settingsItems = computed((): NavigationMenuItem[] => [toItem(SETTINGS_ITEM, '')])

    const userName = computed((): string => appStore.userData?.name || appStore.userData?.username || '')

    const userInitials = computed((): string => initials(userName.value))

    /*
     * "Hotel Lipa · Skrbnik" — the CURRENT tenant and the role in it (a person may be an admin
     * of one tenant and an editor of another); a provider admin is the provider's.
     */
    const userRole = computed((): string => {
        const me = appStore.userData
        if (!me) return ''
        if (me.provider_admin) return t('role.provider_admin')

        const membership = appStore.membershipIn(tenantStore.contextTid)

        return membership ? [membership.name, roleLabel(membership.role)].join(' · ') : ''
    })

    const userMenu = computed(() => [[{
        label:    t('general.sign_out'),
        icon:     'i-lucide-log-out',
        onSelect: () => {
            appStore.setLogout()
            void router.push({ name: 'login' })
        },
    }]])
</script>
